import type { FieldInstanceFields } from "@regrello/graphql-api";
import {
  type RegrelloDataGridV2EditableCellProps,
  useDataGridV2EditableCell,
  useDataGridV2FirstKeystroke,
} from "@regrello/ui-core";
import { toDate } from "date-fns";
import { useCallback, useId, useRef } from "react";
import { RegrelloFormFieldDate } from "../../../../formFields/RegrelloFormFieldDate";
import type { DateFieldPluginFrontendValue } from "./types";

export interface RegrelloEditableCellDateProps {
  /** The field instance that corresponds to the current table column. */
  fieldInstance: FieldInstanceFields;

  /**
   * Whether the date field is a date-only field. If true, the time and timezone portion of the
   * value will be ignored.
   */
  isDateOnlyFieldEnabled: boolean;

  /** The callback to invoke when the user changes the value of the cell. */
  onValueChange: (value: DateFieldPluginFrontendValue) => void;

  /**
   * The current value to display in the cell. This is the value before editing and the value that
   * will be reinstated if editing is canceled.
   */
  value: DateFieldPluginFrontendValue;
}

/** An editable cell in which the user can input a date. Intended for use in `RegrelloDataGridV2`. */
export function RegrelloEditableCellDate({
  api,
  eventKey,
  fieldInstance,
  isDateOnlyFieldEnabled,
  onValueChange,
  value,
}: RegrelloDataGridV2EditableCellProps<RegrelloEditableCellDateProps>) {
  const editableCellRef = useRef<HTMLDivElement>(null);

  if (fieldInstance.spectrumFieldVersion == null) {
    throw new Error("spectrumFieldVersioIn is unexpectedly not defined");
  }

  const { columnWidth, onKeyDownCapture } = useDataGridV2EditableCell({
    api,
    eventKey,
    // These keys are used to cycle through dates/months/years:
    suppressKeyArrowDown: true,
    suppressKeyArrowUp: true,
    suppressKeyArrowLeft: true,
    suppressKeyArrowRight: true,
  });

  const { inputRef } = useDataGridV2FirstKeystroke({
    // We cannot select text in the date input, so don't try to parse the event key.
    eventKey: null,
    onValueChange,
    // It's too difficult to determine which part of the date comes first, and we don't want to get
    // too clever, so just default to null after focusing the input.
    parseEventKey: () => null,
  });

  // (jullman): When the user closes the date picker via selecting an option or clearing, highlight
  // the cell that was just edited. If the cell was closed via Enter (or other key that has actions
  // determined by onKeyDownCapture), the microtask will correct selected cell before the first
  // paint (see onKeyDown).
  const onClose = useCallback(() => {
    const row = api.getFocusedCell()?.rowIndex;
    const columnId = api.getFocusedCell()?.column;
    if (row == null || columnId == null) {
      console.warn("No editing cell found");
      return;
    }
    api.stopEditing();
    api.setFocusedCell(row, columnId);
  }, [api]);

  const focusGroupId = useId();

  return (
    // Date fields are full of content and are difficult to shrink.
    <div ref={editableCellRef} className="min-w-42" style={{ width: columnWidth }}>
      <RegrelloFormFieldDate
        autoFocus={true}
        enableDynamicCalendarPicker={true}
        firstSegmentInitialKeystroke={eventKey}
        fullWidth={true}
        ignoreTimezone={isDateOnlyFieldEnabled}
        inputClassName="rounded-none"
        inputRef={inputRef}
        isDefaultMarginsOmitted={true}
        onBlur={(event) => {
          const nextFocusedElement = event.relatedTarget as HTMLElement | null | undefined;
          const inSameGroup = nextFocusedElement?.closest(`[data-focus-group="${focusGroupId}"]`) != null;

          if (!inSameGroup) {
            api.stopEditing();
          }
        }}
        onChange={(date, _value) => {
          onValueChange(date != null ? toDate(date) : null);
        }}
        onClosePopover={onClose}
        onKeyDownCapture={onKeyDownCapture}
        overrideFocusGroupId={focusGroupId}
        size="x-small"
        value={value}
      />
    </div>
  );
}
